import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { API } from "../App";
import { toast } from "sonner";
import { FileSignature, Loader2, Check, X, Eraser, ArrowLeft } from "lucide-react";

const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-700",
  signed: "bg-emerald-100 text-emerald-700",
  declined: "bg-rose-100 text-rose-700",
  voided: "bg-slate-200 text-slate-600",
};

const SignaturePad = ({ canvasRef, onChange }) => {
  const drawing = useRef(false);
  const start = (e) => { drawing.current = true; draw(e); };
  const end = () => { drawing.current = false; const c = canvasRef.current; if (c) c.getContext("2d").beginPath(); onChange?.(); };
  const draw = (e) => {
    if (!drawing.current) return;
    const c = canvasRef.current; if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = ((e.touches ? e.touches[0].clientX : e.clientX) - rect.left) * (c.width / rect.width);
    const y = ((e.touches ? e.touches[0].clientY : e.clientY) - rect.top) * (c.height / rect.height);
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.strokeStyle = "#0f172a";
    ctx.lineTo(x, y); ctx.stroke(); ctx.beginPath(); ctx.moveTo(x, y);
  };
  return (
    <canvas
      ref={canvasRef} width={520} height={150}
      className="w-full border border-slate-300 rounded-lg bg-white touch-none cursor-crosshair"
      data-testid="signature-canvas"
      onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
      onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
    />
  );
};

const SignDocumentPage = () => {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [doc, setDoc] = useState(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signerName, setSignerName] = useState("");
  const [affirm, setAffirm] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (requestId) {
          const res = await fetch(`${API}/signature-requests/${requestId}`, { credentials: "include" });
          if (res.ok) setDoc(await res.json());
          else toast.error("Document not found or not yours");
        } else {
          const res = await fetch(`${API}/signature-requests/mine`, { credentials: "include" });
          const d = await res.json();
          setList(d.requests || []);
        }
      } catch { toast.error("Failed to load"); } finally { setLoading(false); }
    })();
  }, [requestId]);

  const clearPad = () => {
    const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  const sign = async () => {
    if (!signerName.trim()) { toast.error("Type your full legal name"); return; }
    if (!affirm) { toast.error("Please check the affirmation box"); return; }
    setSubmitting(true);
    try {
      const img = hasDrawn && canvasRef.current ? canvasRef.current.toDataURL("image/png") : null;
      const res = await fetch(`${API}/signature-requests/${requestId}/sign`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ signer_name: signerName.trim(), affirmation: true, signature_image: img }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d?.detail || "Failed to sign");
      toast.success("Signed — thank you!");
      navigate("/sign");
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const decline = async () => {
    const reason = window.prompt("Optionally tell us why you're declining:") || "";
    try {
      const res = await fetch(`${API}/signature-requests/${requestId}/decline`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) throw new Error();
      toast.success("Declined"); navigate("/sign");
    } catch { toast.error("Failed to decline"); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  // LIST VIEW
  if (!requestId) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10" data-testid="sign-list-page">
        <h1 className="text-2xl font-heading font-bold text-slate-900 mb-1 flex items-center gap-2"><FileSignature className="w-6 h-6 text-brand-navy" /> Documents to Sign</h1>
        <p className="text-slate-500 mb-6">Review and e-sign documents from Aspiritech Academy.</p>
        {list.length === 0 && <p className="text-slate-400">You have no documents right now. 🎉</p>}
        <div className="space-y-3">
          {list.map(r => (
            <Card key={r.request_id} className="card-surface" data-testid={`sign-list-row-${r.request_id}`}>
              <CardContent className="py-4 flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-slate-800">{r.title}</p>
                  <p className="text-xs text-slate-400 capitalize">{(r.document_type || "").replace(/_/g, " ")}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={STATUS_STYLE[r.status]}>{r.status}</Badge>
                  {r.status === "pending" && <Button className="btn-primary" onClick={() => navigate(`/sign/${r.request_id}`)} data-testid={`sign-open-${r.request_id}`}>Review &amp; Sign</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // SINGLE DOC VIEW
  if (!doc) return <div className="max-w-2xl mx-auto px-4 py-10"><Link to="/sign" className="text-brand-navy">← Back</Link><p className="mt-4 text-slate-500">Document not available.</p></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="sign-document-page">
      <button onClick={() => navigate("/sign")} className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="text-slate-900 flex items-center gap-2"><FileSignature className="w-5 h-5 text-brand-navy" /> {doc.title}</CardTitle>
          <Badge className={`${STATUS_STYLE[doc.status]} w-fit`}>{doc.status}</Badge>
        </CardHeader>
        <CardContent className="space-y-5">
          {doc.body_text && <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-80 overflow-y-auto whitespace-pre-wrap" data-testid="sign-body-text">{doc.body_text}</div>}

          {doc.status !== "pending" ? (
            <p className="text-sm text-slate-500">This document is <span className="font-medium">{doc.status}</span>{doc.signed_at ? ` (${new Date(doc.signed_at).toLocaleString()})` : ""}.</p>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-slate-700">Type your full legal name *</Label>
                <Input className="input-base" value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Jane A. Doe" data-testid="signer-name-input" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-700">Draw your signature (optional)</Label>
                  <button onClick={clearPad} className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1" data-testid="clear-signature"><Eraser className="w-3 h-3" /> Clear</button>
                </div>
                <SignaturePad canvasRef={canvasRef} onChange={() => setHasDrawn(true)} />
              </div>
              <label className="flex items-start gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={affirm} onChange={(e) => setAffirm(e.target.checked)} className="mt-1 w-4 h-4" data-testid="affirm-checkbox" />
                <span>I affirm that I have read this document and that typing my name and/or drawing my signature above constitutes my legal electronic signature.</span>
              </label>
              <div className="flex gap-2">
                <Button className="btn-primary gap-2" onClick={sign} disabled={submitting} data-testid="submit-signature-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Sign Document</Button>
                <Button variant="outline" className="btn-outline gap-2 border-red-200 text-red-600" onClick={decline} disabled={submitting} data-testid="decline-signature-btn"><X className="w-4 h-4" /> Decline</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SignDocumentPage;
